import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProposalExtraItemType } from '@econtractor/types';
import { useToast } from 'context/ToastContext';
import {
    hasSyncableAdditionalPrices,
    syncAdditionalRowsPrices,
} from '../../model/syncCatalogPrices';

export interface UseUpdateAdditionalPricesParams {
    items: ProposalExtraItemType[];
    rate: number;
    localeNs?: string;
    onChange: (items: ProposalExtraItemType[]) => void;
}

const useUpdateAdditionalPrices = ({
    items,
    rate,
    localeNs = 'Proposal',
    onChange,
}: UseUpdateAdditionalPricesParams) => {
    const { t } = useTranslation([localeNs, 'common']);
    const { showToast } = useToast();
    const [isUpdating, setIsUpdating] = useState(false);

    const canUpdate = hasSyncableAdditionalPrices(items);

    const handleUpdatePrices = async () => {
        if (!canUpdate || isUpdating) return;
        setIsUpdating(true);
        try {
            const next = await syncAdditionalRowsPrices(items, rate);
            onChange(next);
            showToast(t('edit.additional.updatePricesSuccess'), 'success');
        } catch (e) {
            console.error(e);
            showToast(t('common:messError'), 'danger');
        } finally {
            setIsUpdating(false);
        }
    };

    return {
        isUpdating,
        canUpdate,
        handleUpdatePrices,
    };
};

export default useUpdateAdditionalPrices;
